import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const isDev = import.meta.env?.DEV;
const devLog = (...args) => { if (isDev) console.log('[GearControl]', ...args); };

export const useRealtimeWorkOrders = ({ sessionReady = true, contextReady = true } = {}) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const toCamelCase = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj?.map(toCamelCase);
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  };

  const fetchWorkOrders = useCallback(async () => {
    if (!sessionReady || !contextReady) {
      devLog('FETCH SALES SKIPPED — waiting for session/context', { sessionReady, contextReady });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('FETCHING SALES');
      devLog('FETCH SALES START');

      const { data, error: fetchError } = await supabase
        ?.from('work_orders')
        ?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*)
        `)
        ?.order('fecha_ingreso', { ascending: false });

      if (fetchError) throw fetchError;

      const result = toCamelCase(data || []);
      setWorkOrders(result);
      setLastUpdate(new Date());
      devLog('FETCH SALES DONE', 'count:', result?.length);
    } catch (err) {
      console.error('[GearControl] FETCH SALES ERROR', err?.message);
      setError(err?.message || 'Error al cargar órdenes de trabajo');
    } finally {
      setLoading(false);
    }
  }, [sessionReady, contextReady]);

  useEffect(() => {
    if (sessionReady && contextReady) {
      fetchWorkOrders();
    } else {
      // Keep loading=true while waiting for context
      setLoading(true);
    }
  }, [sessionReady, contextReady, fetchWorkOrders]);

  useEffect(() => {
    if (!sessionReady || !contextReady) return;

    const channel = supabase
      ?.channel('work_orders_changes')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_orders' },
        (payload) => {
          devLog('Work order change detected:', payload?.eventType);
          setIsLive(true);
          fetchWorkOrders();
          setTimeout(() => setIsLive(false), 2000);
        }
      )
      ?.subscribe((status) => {
        devLog('work_orders subscription status:', status);
      });

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [sessionReady, contextReady, fetchWorkOrders]);

  return {
    workOrders,
    loading,
    error,
    isLive,
    lastUpdate,
    refetch: fetchWorkOrders
  };
};